import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedProgressProps {
  value: number;
  duration?: number;
  className?: string;
  indicatorClassName?: string;
  showLabel?: boolean;
  labelPosition?: 'inside' | 'outside';
}

export function AnimatedProgress({
  value,
  duration = 1200,
  className,
  indicatorClassName,
  showLabel = false,
  labelPosition = 'outside'
}: AnimatedProgressProps) {
  const [displayValue, setDisplayValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const easeOutExpo = (t: number): number => {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  };

  useEffect(() => {
    if (hasAnimated) {
      setDisplayValue(value);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            startAnimation();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, hasAnimated]);

  const startAnimation = () => {
    startTimeRef.current = null;

    const animate = (currentTime: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(progress);
      
      setDisplayValue(value * easedProgress);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  return (
    <div ref={elementRef} className="relative">
      <div
        className={cn(
          'relative h-2 w-full overflow-hidden rounded-full bg-secondary',
          className
        )}
      >
        <div
          className={cn(
            'h-full rounded-full bg-primary transition-none',
            indicatorClassName
          )}
          style={{ 
            width: `${Math.min(100, displayValue)}%`,
            transition: 'none'
          }}
        />
      </div>
      {showLabel && labelPosition === 'outside' && (
        <span className="absolute -right-1 top-1/2 -translate-y-1/2 translate-x-full pl-2 text-xs font-medium tabular-nums">
          {Math.round(displayValue)}%
        </span>
      )}
    </div>
  );
}
